import React, { useEffect, useState, useRef } from "react";
import { db } from "./firebase";
import { supabase } from "./pages/supabaseClient";
import { collection, getDocs, where, query, orderBy, limit } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { Helmet } from "react-helmet-async";

// Styles (reuse the same look as the logged-in feed)
import "./HomeScreen.css";
import "./SearchButton.css";

// Components
import CompetitionTimer from "./components/CompetitionTimer";

// Assets
import mascotImg from "./assets/App-logo-final.png";

// Utils
import { getCached } from "./utils/simpleCache";

const ONE_HOUR = 60 * 60;

// Logged-out, crawlable version of the home feed.
// No auth-only data (pulse, inbox, leaderboard, saved) is fetched here —
// visitors and Googlebot both see the same public ideas.
const PublicFeed = () => {
  const [loading, setLoading] = useState(true);
  const [rewardedIdeas, setRewardedIdeas] = useState([]);
  const [allIdeas, setAllIdeas] = useState([]);
  const [index, setIndex] = useState(0);

  const navigate = useNavigate();
  const trackRef = useRef();

  const signedUrlFor = async (imagePath) => {
    if (!imagePath) return null;
    return getCached(`img-${imagePath}`, async () => {
      const { data, error } = await supabase.storage.from("images").createSignedUrl(imagePath, ONE_HOUR);
      if (error) { console.error("URL error:", error); return null; }
      return data.signedUrl;
    });
  };

  useEffect(() => {
    const fetchIdeas = async () => {
      try {
        setLoading(true);

        const competitionSnap = await getDocs(
          query(collection(db, "PostedCompetitionIdeas"), where("rank", "in", [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]), orderBy("rank", "asc"))
        );

        // NOTE: the "users" collection requires sign-in (it holds email,
        // balances, etc.), so logged-out visitors can't look authors up
        // there. We use whatever the idea doc itself already carries, and
        // fall back to a neutral label. Once a public "publicProfiles"
        // collection exists, swap this for a lookup against that instead.
        const competitionIdeas = competitionSnap.docs.map((d) => {
          const data = d.data();
          const authorName = data.authorName || data.name || "Trescend member";
          return { id: d.id, ...data, authorName, profileImageUrl: null, imageUrl: null };
        });

        const snap = await getDocs(query(collection(db, "postedIdeas"), orderBy("createdAt", "desc"), limit(50)));
        const allIdeasArray = snap.docs.map((d) => {
          const data = d.data();
          const authorName = data.authorName || data.name || "Trescend member";
          return { id: d.id, ...data, authorName, profileImageUrl: null, imageUrl: null };
        });

        // Show titles/descriptions/authors right away — this is what a
        // crawler's screenshot needs to see, and it shouldn't wait on
        // image URLs. Images are fetched afterward and patched in as they
        // resolve, one Supabase round-trip at a time.
        setRewardedIdeas(competitionIdeas);
        setAllIdeas(allIdeasArray);
        setLoading(false);

        competitionIdeas.forEach((idea) => {
          if (!idea.imagePath) return;
          signedUrlFor(idea.imagePath).then((imageUrl) => {
            if (!imageUrl) return;
            setRewardedIdeas((prev) => prev.map((i) => (i.id === idea.id ? { ...i, imageUrl } : i)));
          });
        });

        allIdeasArray.forEach((idea) => {
          if (!idea.imagePath) return;
          signedUrlFor(idea.imagePath).then((imageUrl) => {
            if (!imageUrl) return;
            setAllIdeas((prev) => prev.map((i) => (i.id === idea.id ? { ...i, imageUrl } : i)));
          });
        });
      } catch (error) {
        console.error("Error fetching ideas:", error);
        setLoading(false);
      }
    };
    fetchIdeas();
  }, []);

  // Auto-scroll carousel (same behavior as the logged-in feed)
  useEffect(() => {
    let interval;
    let isInteracting = false;
    const track = trackRef.current;
    if (!track) return;

    const startAutoScroll = () => {
      interval = setInterval(() => {
        if (!isInteracting && rewardedIdeas.length > 1) {
          setIndex((prev) => (prev + 1) % rewardedIdeas.length);
        }
      }, 5000);
    };

    const pause = () => (isInteracting = true);
    const resume = () => (isInteracting = false);

    startAutoScroll();
    track.addEventListener("mouseenter", pause);
    track.addEventListener("mouseleave", resume);
    track.addEventListener("touchstart", pause);
    track.addEventListener("touchend", () => setTimeout(resume, 1000));

    return () => {
      clearInterval(interval);
      track.removeEventListener("mouseenter", pause);
      track.removeEventListener("mouseleave", resume);
      track.removeEventListener("touchstart", pause);
    };
  }, [rewardedIdeas.length]);

  useEffect(() => {
    if (!trackRef.current || rewardedIdeas.length === 0) return;
    const card = trackRef.current.children[index];
    if (!card) return;
    const style = window.getComputedStyle(trackRef.current);
    const gap = parseInt(style.gap) || 16;
    const cardWidth = card.offsetWidth;
    const containerWidth = trackRef.current.parentElement.offsetWidth;
    const totalTrackWidth = trackRef.current.scrollWidth;

    let moveDistance = (cardWidth + gap) * index;
    if (window.innerWidth >= 768) {
      const centerOffset = (containerWidth / 2) - (cardWidth / 2);
      moveDistance = moveDistance - centerOffset;
      const maxMove = totalTrackWidth - containerWidth;
      moveDistance = Math.max(0, Math.min(moveDistance, maxMove));
    }
    trackRef.current.style.transform = `translateX(${-moveDistance}px)`;
  }, [index, rewardedIdeas.length]);

  return (
    <div className="homepage-container">
      <Helmet>
        <title>Trescend — Where Ideas Pay Off</title>
        <meta
          name="description"
          content="Browse original ideas shared by the Trescend community. Read them, see the best ones rewarded in idea competitions, and post your own once you join."
        />
      </Helmet>

      <header className="header-home">
        <div className="trescend-header-inner">
          <div className="header-top-row">
            <div className="header-left">
              <img src={mascotImg} alt="Trescend mascot" className="header-mascot-img" />
            </div>

            <div className="header-actions">
              <button className="pulse-group" onClick={() => navigate("/Login_page")} style={{ border: "none", background: "transparent", cursor: "pointer", color: "inherit" }}>
                Log in
              </button>
              <button className="pulse-group" onClick={() => navigate("/Get_started")} style={{ border: "none", background: "transparent", cursor: "pointer", color: "inherit" }}>
                Sign up
              </button>
            </div>
          </div>

          <div className="search-row-container">
            <div className="search-button-wrapper-home" onClick={() => navigate("/Login_page")}>
              <span className="search-button-text-home">Log in to search for journeys</span>
            </div>
          </div>
        </div>
      </header>

      <p style={{ padding: "0 16px", color: "#ccc", maxWidth: 700 }}>
        Trescend is a place to share ideas, get feedback, and see the best ones rewarded.
        Browse what people are posting below — log in to post your own, comment, or enter a competition.
      </p>

      <CompetitionTimer loading={loading} />

      <section className="section rewarded-ideas-section">
        <h2 className="section-title-home rewarded-ideas-title">Rewarded Ideas</h2>
        <div className={`rewarded-carousel ${loading ? "loading" : ""}`}>
          <div className="carousel-track" ref={trackRef}>
            {loading ? (
              [1, 2, 3].map((_, i) => (
                <div key={`skeleton-${i}`} className="reward-card-premium skeleton-reward-card">
                  <div className="card-header-row"><div className="skeleton-pill skeleton-shimmer"></div><div className="skeleton-text-small skeleton-shimmer" style={{ width: '40px' }}></div></div>
                  <div className="name-glass-box skeleton-glass"><div className="skeleton-avatar skeleton-shimmer"></div><div className="skeleton-text-name skeleton-shimmer"></div></div>
                  <div className="category-section-tight"><div className="skeleton-text-small skeleton-shimmer" style={{ width: '40%' }}></div><div className="privacy-divider-line" style={{ opacity: 0.1 }}></div><div className="skeleton-text-block skeleton-shimmer"></div></div>
                </div>
              ))
            ) : (
              rewardedIdeas.map((idea, i) => {
                const rankClass = idea.rank === 1 ? 'rank-1' : idea.rank === 2 ? 'rank-2' : idea.rank === 3 ? 'rank-3' : 'rank-default';
                return (
                  <article key={idea.id || i} className={`reward-card-premium ${rankClass}`}>
                    <p className="card-dateline">Idea competition · final edition</p>
                    <div className="card-header-row">
                      <div className="winner-pill-yellow">#{idea.rank} winner</div>
                      <div className="card-amount-top">Awarded INR {idea.reward}</div>
                    </div>
                    <div className="name-glass-box">
                      <div className="profile-container-mini">
                        {idea.profileImageUrl ? <img src={idea.profileImageUrl} alt="" className="profile-img-premium" /> : <div className="avatar-placeholder-small">{idea.authorName?.[0]}</div>}
                      </div>
                      <span className="author-name-premium">{idea.authorName}</span>
                    </div>
                    <div className="category-section-tight">
                      <div className="category-label-standalone">{idea.tags?.[0] || "Media"}</div>
                      <div className="protection-stamp">
                        <div className="stamp-circle" aria-hidden="true">
                          <span>Certified<br />Original</span>
                        </div>
                        <div className="stamp-text">
                          <p className="card-privacy-notice">Original idea protected under contest terms.</p>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })
            )}
          </div>

          {!loading && rewardedIdeas.length > 1 && (
            <div className="carousel-controls">
              <button className="arrow shimmer-arrow" onClick={() => setIndex((prev) => (prev - 1 + rewardedIdeas.length) % rewardedIdeas.length)}><FaChevronLeft /></button>
              <div className="carousel-dots">{rewardedIdeas.map((_, dotIndex) => (<span key={dotIndex} className={`dot ${index === dotIndex ? "active" : ""}`} onClick={() => setIndex(dotIndex)} />))}</div>
              <button className="arrow shimmer-arrow" onClick={() => setIndex((prev) => (prev + 1) % rewardedIdeas.length)}><FaChevronRight /></button>
            </div>
          )}
        </div>
      </section>

      <section className="section">
        <h2 className="section-title-home">All Ideas</h2>
        <div className="idea-list">
          {allIdeas.map((idea, i) => (
            <article className="idea-card" key={i} onClick={() => navigate(`/view-idea/${idea.id}`, { state: { idea } })}>
              <header className="card-top">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {idea.profileImageUrl ? <img src={idea.profileImageUrl} alt={idea.authorName} className="profile-image" /> : <span className="avatar">{idea.authorName?.[0]}</span>}
                  <span className="user-name-home">{idea.authorName}</span>
                </div>
              </header>
              <div className="card-content">
                {idea.imageUrl && <img src={idea.imageUrl} alt={idea.description} className="idea-image" loading="lazy" />}
                <div className="card-body">
                  {idea.title && <h3 className="idea-title">{idea.title}</h3>}
                  <div className="tags-home">{idea.tags?.map((t, idx) => (<span className="tag" key={idx}>{t}</span>))}</div>
                </div>
              </div>
              {idea.description && <p className="description description-truncated">{idea.description}</p>}
            </article>
          ))}
        </div>
      </section>

      <div style={{ textAlign: "center", padding: "24px 16px 48px" }}>
        <button
          onClick={() => navigate("/Get_started")}
          style={{ background: "#f5c542", border: "none", borderRadius: 8, padding: "12px 24px", fontWeight: 600, cursor: "pointer" }}
        >
          Join Trescend to post your own idea
        </button>
      </div>
    </div>
  );
};

export default PublicFeed;
